import type { ChangeTone } from '@/design'

import { LIKE_FOR_LIKE, type DeltaPresentation } from './dataQuality'

/**
 * The attributes that say how a change is shown — whether it straddles the baseline, its
 * tooltip and the notice that describes it — on whichever element carries the change: a
 * `DeltaBadge`, or a KPI tile whose delta the design system's `KpiCard` draws. The one
 * place a straddling comparison's wiring is decided.
 */
export function straddleAttributes(presentation: DeltaPresentation) {
  return {
    'data-baseline': presentation.neutral ? ('straddles' as const) : undefined,
    title: presentation.title,
    'aria-describedby': presentation.describedBy,
  }
}

/**
 * A previous-period change beneath a figure, in a table or a totals row: coloured by
 * what it means (`tone`), or neutral — the baseline notice its tooltip and description —
 * when the comparison straddles the tracking baseline (`presentation`). Shared by the
 * Traffic page's first-party tree and the Attribution page's tables, so the two can
 * never show a straddling change differently.
 */
export function DeltaBadge({
  text,
  tone,
  presentation = LIKE_FOR_LIKE,
  className,
}: {
  text: string
  tone: ChangeTone
  presentation?: DeltaPresentation
  className: string
}) {
  return (
    <small className={className} data-tone={presentation.neutral ? 'neutral' : tone} {...straddleAttributes(presentation)}>
      {text}
    </small>
  )
}
